/*
 * ////////////////////////////////////////////////////////////////////////////////
 * //
 * // This software system consists of computer software and documentation.
 * // It contains trade secrets and confidential information which are proprietary
 * // to Everi Games Inc.  Its use or disclosure in whole or in part without
 * // the express written permission of Everi Games Inc. is prohibited.
 * //
 * // This software system is also an unpublished work protected under the copyright
 * // laws of the United States of America.
 * //
 * // Copyright © 2019 Everi Games Inc.  All Rights Reserved
 * //
 * ////////////////////////////////////////////////////////////////////////////////
 */
import Currency, { CurrencyType } from '../../Localization/Currency';
import LocalizationComponent from '../../Localization/LocalizationComponent';
import ObservableCurrencyComponent from '../../Localization/ObservableCurrencyComponent';
import BetManager from '../../Playbar/BetManager';
import PlaybarManager from '../../Playbar/PlaybarManager';
import Communicator from '../../Server/Communicator';
import { ErrorPrompt } from '../../Server/Error/ErrorPrompt';
import IErrorParams from '../../Server/Error/IErrorParams';
import { IPlayState } from '../../Server/Object/PlayResult';
import PlayResultComponent from '../../Server/PlayResultComponent';
import { RGSErrorCode } from '../../Server/RGSErrorCode';
import StateComponent from '../../StateManager/StateComponent';
import StateDependencyComponent from '../../StateManager/StateDependencyComponent';
import InjectableComponent from '../../Utility/Component/ComponentReference/InjectableComponent';
import { ReferenceGroup } from '../../Utility/Component/ComponentReference/ReferenceGroup';
import ObservableBooleanComponent from '../../Utility/Component/Observable/ObservableBooleanComponent';
import { IPopupOptions } from '../../Utility/Component/Popup/IPopupModal';
import PopupModal from '../../Utility/Component/Popup/PopupModal';
import CLog from '../../Utility/ComponentLogger';
import TypeStrings from '../../Utility/TypeStrings';
import { DisplayKey } from '../DisplayKey';
import GameSetup from '../GameSetup';
import RecallManager from '../RecallManager';
import RecoveryManager from '../RecoveryManager';
import IPlatformAdapter from './IPlatformAdapter';
import {
    ErrorCategory,
    ErrorSeverity,
    GCM,
    GCMOptionSrc,
    GCMOptionType,
    IGCMBalances,
    IGCMDelegate,
    IGCMDelegateContainer,
    INyxClientConfig,
    INyxWindow,
    ISGDBalanceDetails,
    ISGDError,
} from './NyxInterfaces';

export default class NyxAdapter implements IPlatformAdapter, IGCMDelegate
{
    private _betManagerRef: InjectableComponent<BetManager> = new InjectableComponent();
    private get betManager(): BetManager { return this._betManagerRef.Value; }

    private _communicator: InjectableComponent<Communicator> = new InjectableComponent();
    private get communicator(): Communicator { return this._communicator.Value; }

    private _gameSetupRef: InjectableComponent<GameSetup> = new InjectableComponent();
    private get gameSetup(): GameSetup { return this._gameSetupRef.Value; }

    private _localRef: InjectableComponent<LocalizationComponent> = new InjectableComponent();
    private get local(): LocalizationComponent { return this._localRef.Value; }

    private _playbarRef: InjectableComponent<PlaybarManager> = new InjectableComponent();

    private _playResultRef: InjectableComponent<PlayResultComponent> = new InjectableComponent();
    private get playResult(): PlayResultComponent { return this._playResultRef.Value; }

    private _popupModalRef: InjectableComponent<PopupModal> = new InjectableComponent();
    private get popupModal(): PopupModal { return this._popupModalRef.Value; }

    private _recallManagerRef: InjectableComponent<RecallManager> = new InjectableComponent();
    private get recallManager(): RecallManager { return this._recallManagerRef.Value; }

    private _recoveryManagerRef: InjectableComponent<RecoveryManager> = new InjectableComponent();
    private get recoveryManager(): RecoveryManager { return this._recoveryManagerRef.Value; }

    private _balanceObservable: InjectableComponent<ObservableCurrencyComponent>
        = new InjectableComponent(ReferenceGroup.MainGame, "BetManager.Balance");
    private _betObservable: InjectableComponent<ObservableCurrencyComponent>
        = new InjectableComponent(ReferenceGroup.MainGame, "BetManager.Bet");
    private _muteObservable: InjectableComponent<ObservableBooleanComponent>
        = new InjectableComponent(ReferenceGroup.MainGame, "AudioManager.IsMute");
    private _turboObservable: InjectableComponent<ObservableBooleanComponent>
        = new InjectableComponent(ReferenceGroup.MainGame, "AutoPlay.IsTurboMode");

    private _gcm: GCM = null;
    private _hasShownRecover: boolean = false;
    private _shouldRetry: boolean = false;
    private _pendingRound: number = -1;
    private _pendingPlay: number = -1;

    private readonly SGD_ERROR_MAP: any;
    private readonly EVERI_ERROR_MAP: any;

    constructor()
    {
        this._setContexts();

        const nyxWindow: INyxWindow = (window as any) as INyxWindow;
        const clientConfig: INyxClientConfig = nyxWindow.clientConfig.clientconfig;

        this.SGD_ERROR_MAP = clientConfig.sgdErrors;
        this.EVERI_ERROR_MAP = clientConfig.everiErrors;

        const gcmDelegate: IGCMDelegateContainer = nyxWindow._gcmDelegate;
        gcmDelegate.delegates.push(this);

        this._gcm = gcmDelegate.gcm;

        this.gameSetup.OnSetupReceived(this._setupReceived);
    }

    private _setContexts(): void
    {
        this._betManagerRef.SetContext(BetManager);
        this._communicator.SetContext(Communicator);
        this._gameSetupRef.SetContext(GameSetup);
        this._localRef.SetContext(LocalizationComponent);
        this._playbarRef.SetContext(PlaybarManager);
        this._playResultRef.SetContext(PlayResultComponent);
        this._popupModalRef.SetContext(PopupModal);
        this._recallManagerRef.SetContext(RecallManager);
        this._recoveryManagerRef.SetContext(RecoveryManager);

        this._balanceObservable.SetContext(ObservableCurrencyComponent);
        this._betObservable.SetContext(ObservableCurrencyComponent);
        this._muteObservable.SetContext(ObservableBooleanComponent);
        this._turboObservable.SetContext(ObservableBooleanComponent);
    }

    private _getErrorCategory(errorCode: string, errorParams: IErrorParams): string
    {
        switch (errorCode)
        {
            case RGSErrorCode[RGSErrorCode.CONNECTION_ERROR]:
                return ErrorCategory[ErrorCategory.CONNECTION_ERROR];
            case RGSErrorCode[RGSErrorCode.AUTHENTICATION]:
            case RGSErrorCode[RGSErrorCode.AUTHORIZATION]:
            case RGSErrorCode[RGSErrorCode.PRIZEGEN_ERROR]:
            case RGSErrorCode[RGSErrorCode.UNDEFINED_ERROR]:
            case RGSErrorCode[RGSErrorCode.WALLET_ACCESS_TOKEN_EXPIRED]:
            case RGSErrorCode[RGSErrorCode.WALLET_REALITY_CHECK_EXTENSION]:
            case RGSErrorCode[RGSErrorCode.WALLET_GEOLOCATION_FAILURE]:
            case RGSErrorCode[RGSErrorCode.WALLET_NO_WAGER_DATA]:
                return ErrorCategory[ErrorCategory.CRITICAL];
            case RGSErrorCode[RGSErrorCode.INSUFFICIENT_FUND]:
                return ErrorCategory[ErrorCategory.INSUFFICIENT_FUNDS];
            case RGSErrorCode[RGSErrorCode.GAME_NOT_STARTED]:
            case RGSErrorCode[RGSErrorCode.RETRY_REQUEST_PLAY]:
            case RGSErrorCode[RGSErrorCode.RGS_ERROR]:
            case RGSErrorCode[RGSErrorCode.UPDATE_CONFIG]:
            case RGSErrorCode[RGSErrorCode.BAD_PLAYER_STATE]:
            case RGSErrorCode[RGSErrorCode.WALLET_ERROR]:
                return ErrorCategory[ErrorCategory.NON_RECOVERABLE_ERROR];
            case RGSErrorCode[RGSErrorCode.INVALID_REQUEST]:
                if (errorParams.prompts.indexOf(ErrorPrompt.ExitToLobby) < 0)
                {
                    return ErrorCategory[ErrorCategory.CRITICAL];
                }
                else
                {
                    return ErrorCategory[ErrorCategory.RECOVERABLE_ERROR];
                }
            default:
                return null;

        }
    }

    private _getErrorMessage(errorCode: string, errorParams: IErrorParams): string
    {
        if (!errorParams.textCode)
        {
            return null;
        }

        let message: string = this.local.GetStringResource(`Errors/${errorParams.textCode}`);

        for (let promptIndex in errorParams.prompts)
        {
            let prompt: string = errorParams.prompts[promptIndex];

            // Prompted to exit to lobby, but Lobby is not available per configuration
            if (prompt === "ExitToLobby" && this._gameSetupRef.Value.ShouldDisplay(DisplayKey.Lobby) === false)
            {
                message += ` ${this.local.GetStringResource("Errors/Please_return_to_lobby")}`;
            }
        }

        return message;
    }

    private _getErrorSeverity(errorCode: string, errorParams: IErrorParams): string
    {
        switch (errorCode)
        {
            case RGSErrorCode[RGSErrorCode.INSUFFICIENT_FUND]:
                return ErrorSeverity[ErrorSeverity.INFO];
            default:
                return ErrorSeverity[ErrorSeverity.ERROR];
        }
    }

    private _getGCMBalances(balances: ISGDBalanceDetails, wagerMc?: number): IGCMBalances
    {
        let realAmt: number = 0;
        let bonusAmt: number = 0;

        if (balances && balances.balance && balances.balance.realMc != null && balances.balance.bonusMc != null)
        {
            realAmt = balances.balance.realMc;
            bonusAmt = balances.balance.bonusMc;
        }
        else
        {
            realAmt = this.betManager.GetBalance().GetValue(CurrencyType.MC);
        }

        if (wagerMc)
        {
            bonusAmt -= wagerMc;
            if (bonusAmt < 0)
            {
                realAmt += bonusAmt;
                bonusAmt = 0;
            }
        }

        return {
            CASH:
            {
                amount: Currency.ConvertMCToCash(realAmt)
            },
            BONUS:
            {
                amount: Currency.ConvertMCToCash(bonusAmt)
            }
        };
    }

    private _getRegOptionValue(val: boolean | string): boolean
    {
        if (typeof(val) === TypeStrings.boolean)
        {
            return val as boolean;
        }
        else if (!(val === '' || (val as string).toLowerCase() === 'false'))
        {
            return true;
        }

        return false;
    }

    private _defaultReportError(errorCode: string, errorParams: IErrorParams, popupOptions?: IPopupOptions): void
    {
        let message: string = this.local.GetStringResource(`Errors/${errorParams.textCode}`);
        if (!popupOptions)
        {
            popupOptions = { MessageId: errorCode };

            for (let promptIndex in errorParams.prompts)
            {
                const prompt: string = errorParams.prompts[promptIndex];

                // Prompted to exit to lobby, but Lobby is not available per configuration
                if (prompt === "ExitToLobby" && this._gameSetupRef.Value.ShouldDisplay(DisplayKey.Lobby) === false)
                {
                    message += ` ${this.local.GetStringResource("Errors/Please_return_to_lobby")}`;

                    // If reload is not already one of the options, make it one.
                    if (errorParams.prompts.indexOf("Reload") !== -1) { continue; }
                    errorParams.prompts[promptIndex] = "Reload";
                }

                errorParams.handlePrompt(errorParams.prompts[promptIndex], popupOptions, parseInt(promptIndex, 10));
            }
        }
        popupOptions.Title = this.local.GetStringResource(`Errors/Title`);

        if (errorParams && errorParams.traceId)
        {
            message
                += `\r\n${this.local.GetStringResource("Errors/Server_Error_ID_")}${errorParams.traceId}`;
        }

        this._popupModalRef.WithValue(popupModal => popupModal.Show(message, popupOptions));
    }

    private _setupReceived = (): void =>
    {
        this._gcm.paidUpdate(0);

        const balances: ISGDBalanceDetails = this.gameSetup.LastPlayState.balanceDetails;
        this._gcm.balancesUpdate(this._getGCMBalances(balances));

        this._betObservable.Value.OnChanged((): void =>
        {
            this.betManager.unschedule(this._updateStake);
            this.betManager.scheduleOnce(this._updateStake, 0.1);
        });
        this._gcm.stakeUpdate(this.betManager.CurrentBet.GetValue(CurrencyType.CASH));

        if (this.gameSetup.ShouldDisplay(DisplayKey.Turbo))
        {
            const turboVal: boolean = this._getRegOptionValue(this._gcm.regOption(GCMOptionType.TURBO));
            this._turboObservable.Value.Value = turboVal;
            this._turboObservable.Value.OnChanged((): void =>
            {
                this._gcm.optionHasChanged(GCMOptionType.TURBO, GCMOptionSrc.GAME, this._turboObservable.Value.Value);
            });
        }

        if (this.gameSetup.ShouldDisplay(DisplayKey.Sound))
        {
            const muteVal: boolean = this._getRegOptionValue(this._gcm.regOption(GCMOptionType.MUTE));
            this._muteObservable.Value.Value = muteVal;
            this._muteObservable.Value.OnChanged((): void =>
            {
                this._gcm.optionHasChanged(GCMOptionType.MUTE, GCMOptionSrc.GAME, this._muteObservable.Value.Value);
            });
        }

        if (this.gameSetup.LastPlayState.platformNotification)
        {
            this.WalletNotification(this.gameSetup.LastPlayState.platformNotification);
        }
    }

    private _updateStake = (): void =>
    {
        this._gcm.stakeUpdate(this.betManager.CurrentBet.GetValue(CurrencyType.CASH));
    }

    //#region IPlatformAdapter Implementation

    public GameLoaded(): void
    {
        this._playbarRef.WithValue(playbar => playbar.ToggleOff());
        this._gcm.gameReady();
    }

    public WagerRequested(state: StateComponent): void
    {
        if (!this._gcm.isGameReady) { this._gcm.gameReady(); }

        if (this.recoveryManager.Recovering && !this._hasShownRecover)
        {
            this._hasShownRecover = true;
            this._gcm.handleError(
                ErrorCategory[ErrorCategory.RECOVERABLE_ERROR],
                ErrorSeverity[ErrorSeverity.INFO],
                "",
                this.local.GetStringResource("Errors/NyxRecovery"));
        }

        this._playbarRef.WithValue(playbar => playbar.ToggleOff());
        this._gcm.paidUpdate(0);
        this._gcm.gameAnimationStart();

        if (state.IsCurrentState() && state.ReferenceGroup === ReferenceGroup.MainGame)
        {
            const balanceDetails: ISGDBalanceDetails = this.playResult.PlayState
                ? this.playResult.PlayState.balanceDetails
                : this.gameSetup.LastPlayState.balanceDetails;
            this._gcm.balancesUpdate(
                this._getGCMBalances(balanceDetails, 
                    this.betManager.CurrentBet.GetValue(CurrencyType.MC)));
        }
    }

    public WagerMade(): void
    {
        // stub: not used
    }

    public ResultDisplayed(component: StateDependencyComponent, state: StateComponent): boolean
    {
        if (this._playResultRef.Value.ResultData == null)
        {
            return false;
        }

        if (this.playResult.PlayState.platformNotification)
        {
            this.WalletNotification(this.playResult.PlayState.platformNotification);
        }

        const balanceDetails: ISGDBalanceDetails = this.playResult.PlayState.balanceDetails;
        this._gcm.balancesUpdate(this._getGCMBalances(balanceDetails));
        this._gcm.paidUpdate(Currency.ConvertMCToCash(this.playResult.PlayState.winMc));
        this._gcm.gameAnimationComplete(() =>
        {
            // tslint:disable-next-line:no-string-literal
            component['completeState'](state);
            this._playbarRef.WithValue(playbar => playbar.ToggleOn());
        });

        return true;
    }

    public ReportError(errorCode: string, errorParams: IErrorParams, popupOptions?: IPopupOptions): void
    {
        let errorCategory: string = null;
        let errorMessage: string = null;
        let errorSeverity: string = null;
        let params: any = null;

        if (errorCode === RGSErrorCode[RGSErrorCode.INSUFFICIENT_FUND])
        {
            errorParams.walletError = "{ \"code\": \"1006\", \"msg\": \"\" }";
            this._shouldRetry = errorParams.traceId ? true : false;
        }
        else if (errorParams.textCode !== null && errorParams.textCode !== undefined && errorParams.textCode === "5")
        {
            errorParams.walletError = "{ \"code\": \"5\", \"msg\": \"\" }";
        }
        else if (errorCode === RGSErrorCode[RGSErrorCode.WALLET_WAGER_ROLLBACK_COMPLETED])
        {
            errorCategory = "MULTI_CHOICE_DIALOG";
            errorSeverity = "ERROR";
            errorMessage = this.local.GetStringResource('ErrorsSGD/2');
            params =
            {
                options: [ 'Retry', 'Exit' ]
            };

            let pendingPlayData: IPlayState = JSON.parse(errorParams.rgsData) as IPlayState;
            this._pendingRound = pendingPlayData.playResult.PlaySequenceNumber;
            this._pendingPlay = pendingPlayData.playResult.playId;

            this._shouldRetry = true;
        }

        if (errorParams.walletError)
        {
            try {
                let walletError: ISGDError = JSON.parse(errorParams.walletError) as ISGDError;
                errorCategory = this.SGD_ERROR_MAP[`${walletError.code}`].category;
                errorMessage = this.local.GetStringResource(this.SGD_ERROR_MAP[`${walletError.code}`].message);
                errorSeverity = this.SGD_ERROR_MAP[`${walletError.code}`].severity;
            } catch (ex) {
                CLog.error(`Malformed wallet error obj: ${errorParams.walletError}`);
            }
        }
        else if (!errorCategory && !errorSeverity)
        {
            errorCategory = this.EVERI_ERROR_MAP[`${errorCode}`].category;
            errorSeverity = this.EVERI_ERROR_MAP[`${errorCode}`].severity;
        }

        errorCategory = errorCategory === null ? this._getErrorCategory(errorCode, errorParams) : errorCategory;
        errorMessage = errorMessage === null ? this._getErrorMessage(errorCode, errorParams) : errorMessage;
        errorSeverity = errorSeverity === null ? this._getErrorSeverity(errorCode, errorParams) : errorSeverity;

        if (errorCategory && errorMessage)
        {
            if (!this._gcm.isGameReady) { this._gcm.gameReady(); }
            this._gcm.handleError(errorCategory, errorSeverity, '', errorMessage, params);
        }
        else
        {
            this._defaultReportError(errorCode, errorParams, popupOptions);
        }
    }

    public WalletNotification(notification: string): void
    {
        if (!this.recoveryManager.Recovering && !this.recallManager.Recalling)
        {
            this._gcm.handleMessageTrigger(notification);
        }
    }

    //#endregion

    //#region GCM.js "Game" Implementation

    public balancesHasChanged(balances: IGCMBalances): void
    {
        this._balanceObservable.Value.SetValue(balances.CASH.amount + (balances.BONUS ? balances.BONUS.amount : 0),
            CurrencyType.CASH);
    }

    public gameRevealed(): void
    {
        this._playbarRef.WithValue(playbar => playbar.ToggleOn());
    }

    public optionHasChanged(optionType: string, newValue: boolean): void
    {
        switch (optionType)
        {
            case GCMOptionType.TURBO:
                this._turboObservable.Value.Value = newValue;
                break;
            case GCMOptionType.MUTE:
                this._muteObservable.Value.Value = newValue;
                break;
        }
    }

    public resume(errorParamIndex: number): void
    {
        if (this._shouldRetry && !errorParamIndex)
        {
            this._shouldRetry = false;
            this.communicator.RequestPlayResult(
                this.playResult.OnResultHandler.bind(this.playResult),
                this._pendingRound,
                this._pendingPlay
            );

            this._pendingRound = -1;
            this._pendingPlay = -1;
        }
        if (errorParamIndex)
        {
            this.gameSetup.LobbyWindow.location.href = this.gameSetup.LobbyURL;
        }
        else
        {
            this._playbarRef.WithValue(playbar => playbar.ToggleOn());
        }
    }

    //#endregion
}
